import { useMemo } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import { LinkButton } from '@/components'
import { formatDateCell } from '@/components/common/Table/utils'
import { OverflowList } from '@/components/molecules/OverflowList/OverflowList'
import { useGlobalDrawerStore } from '@/stores/useGlobalDrawerStore/useGlobalDrawerStore'
import { InvoiceBill } from '@/hooks'
import { getUnitSymbol } from '@/hooks/useApi/utils/processUsageCostResponse'
import { formatNumber } from '@/utils/formatValueWithUnit'
import { InvoiceBillsLabelsMap } from './useInvoiceBillsLabelsMap'
import { BillNotes } from './BillNotes'
import CommodityBadge from '@/components/molecules/CommodityBadge/CommodityBadge'

export const useColumns = (
    invoiceId: string,
    labelsMap: InvoiceBillsLabelsMap,
    data?: InvoiceBill[]
): ColumnDef<InvoiceBill>[] => {
    const { t } = useTranslation('translations')
    const { setDrawerState } = useGlobalDrawerStore()

    return useMemo(() => {
        const columns: ColumnDef<InvoiceBill>[] = [
            {
                id: 'usn',
                accessorKey: 'usn',
                header: labelsMap['usn'],
            },
            {
                id: 'usnAccountName',
                accessorKey: 'usnAccountName',
                header: labelsMap['usnAccountName'],
            },
            {
                id: 'meters',
                accessorKey: 'meters',
                header: labelsMap['meters'],
                cell: ({ cell }) => {
                    const values = cell.getValue() as
                        | InvoiceBill['meters']
                        | undefined

                    const meterNumbers = values?.map(
                        (meter) => meter.meterNumber
                    ) as unknown[]

                    if (!values) {
                        return null
                    }
                    return (
                        <OverflowList
                            items={meterNumbers}
                            name={'meters'}
                            renderItem={(item) => item as string}
                        />
                    )
                },
            },
            {
                id: 'startDate',
                accessorKey: 'startDate',
                header: labelsMap['startDate'],
                sortingFn: 'datetime',
                cell: formatDateCell as ColumnDef<InvoiceBill>['cell'],
            },
            {
                id: 'endDate',
                accessorKey: 'endDate',
                header: labelsMap['endDate'],
                sortingFn: 'datetime',
                cell: formatDateCell as ColumnDef<InvoiceBill>['cell'],
            },
            {
                id: 'commodity',
                accessorKey: 'commodity',
                header: labelsMap['commodity'],
                cell: ({ cell }) => {
                    const value = cell.getValue() as
                        | InvoiceBill['commodity']
                        | undefined

                    if (!value) {
                        return null
                    }

                    return (
                        <div className="flex items-center gap-1">
                            <CommodityBadge
                                label={value.commodityName}
                                commodityTypeId={value.commodityTypeId}
                            />
                        </div>
                    )
                },
            },
            {
                id: 'usage',
                accessorKey: 'usage',
                header: labelsMap['usage'],
                cell: ({ cell }) => {
                    const value = cell.getValue() as
                        | InvoiceBill['usage']
                        | undefined

                    if (!value) {
                        return null
                    }

                    return `${formatNumber(value.value)} ${getUnitSymbol(value.unit)}`
                },
            },
            {
                id: 'amount',
                accessorKey: 'amount',
                header: () => {
                    if (!data || !data.length) {
                        return labelsMap['amount']
                    }

                    const unit = getUnitSymbol(data[0].amount.unit)

                    return `${labelsMap['amount']} (${unit})`
                },
                cell: ({ cell }) => {
                    const value = cell.getValue() as
                        | InvoiceBill['amount']
                        | undefined

                    if (!value) {
                        return null
                    }
                    return formatNumber(value.value)
                },
            },
            {
                id: 'notes',
                accessorKey: 'notesCount',
                header: () => {
                    return (
                        <div className="grow text-right">
                            {labelsMap['notesCount']}
                        </div>
                    )
                },
                cell: ({ cell, row }) => {
                    const value = cell.getValue() as
                        | InvoiceBill['notesCount']
                        | undefined

                    if (!value) {
                        return null
                    }

                    return (
                        <div className="flex justify-end">
                            <LinkButton
                                iconAfter="sticky_note_2"
                                variant="secondary"
                                label={value}
                                name={''}
                                onClick={(event) => {
                                    event.stopPropagation()
                                    event.preventDefault()

                                    const invoiceBill = row.original
                                    const title = `${t('invoice_table.notesTitle')} (USN ${invoiceBill.usn})`

                                    setDrawerState(
                                        title,
                                        <BillNotes
                                            usn={invoiceBill.usn}
                                            invoiceId={invoiceId}
                                            billId={invoiceBill.billId.toString()}
                                        />,
                                        true
                                    )
                                }}
                            />
                        </div>
                    )
                },
                enableSorting: false,
            },
        ]

        return columns
    }, [data, invoiceId, labelsMap, setDrawerState, t])
}
